import type { TemplateFormWithFields } from '@common/types/quoteTemplates';
import type { TemplateFormField, TemplateFormFieldPayload } from '@common/types/templateFormFields';
import type {
    FormGroup,
    TemplateFormMetadata,
    TemplateFormPropertiesUpdate,
    TemplateFormResponse,
} from '@common/types/templateForms';
import axiosInstance from '@/services/axiosInstance';

interface TemplateFormWritePayload {
    templateFormType: string;
    headerDescription: string | null;
    footerTemplate: string | null;
    templateFormFields: TemplateFormFieldPayload[];
    formGroups?: FormGroup[] | null;
    metadata?: TemplateFormMetadata | null;
    status?: string;
}

export interface Step4TemplateFormPayload extends TemplateFormWritePayload {
    quoteTemplateId?: number;
    templateFormId?: string;
}

export type { TemplateFormWritePayload };

export async function getQuoteTemplateForms(
    quoteTemplateId: number | string,
    includeDeletedTemplateFields = false,
) {
    return axiosInstance.get<TemplateFormResponse>(
        `api/v1/quote-templates/${quoteTemplateId}/quote-template-form?includeDeletedTemplateFields=${includeDeletedTemplateFields}`,
    );
}

export async function getIntakeTemplateForms(
    quoteTemplateId: number | string,
    includeDeleted?: boolean,
) {
    const url = includeDeleted
        ? `api/v1/quote-templates/${quoteTemplateId}/intake-template-form?includeDeletedTemplateFields=true`
        : `api/v1/quote-templates/${quoteTemplateId}/intake-template-form`;
    return axiosInstance.get<TemplateFormResponse>(url);
}

export async function getIntakeTemplateFormsWithDeleted(quoteTemplateId: number | string) {
    return axiosInstance.get<TemplateFormResponse>(
        `api/v1/quote-templates/${quoteTemplateId}/intake-template-form?includeDeletedTemplateFields=true`,
    );
}

export async function createTemplateForm(
    quoteTemplateId: number | string,
    payload: TemplateFormWritePayload,
) {
    return axiosInstance.post<TemplateFormWithFields>(
        `api/v1/quote-templates/${quoteTemplateId}/template-forms`,
        payload,
    );
}

export async function updateTemplateForm(
    quoteTemplateId: number | string,
    templateFormId: string | undefined,
    payload: Omit<TemplateFormWritePayload, 'status'>,
) {
    return axiosInstance.put<TemplateFormWithFields>(
        `api/v1/quote-templates/${quoteTemplateId}/template-forms/${templateFormId}`,
        payload,
    );
}

/** Saves the form's own properties without its fields. Only the properties sent are changed */
export async function updateTemplateFormProperties(
    quoteTemplateId: number | string,
    templateFormId: string | undefined,
    payload: TemplateFormPropertiesUpdate,
) {
    return axiosInstance.patch<void>(
        `api/v1/quote-templates/${quoteTemplateId}/template-forms/${templateFormId}`,
        payload,
    );
}

/** Creates the fields without an ID and updates the rest. Returns the whole form */
export async function saveFormFields(
    quoteTemplateId: number | string,
    templateFormId: string | undefined,
    templateFormFields: TemplateFormFieldPayload[],
) {
    return axiosInstance.post<TemplateFormWithFields>(
        `api/v1/quote-templates/${quoteTemplateId}/template-forms/${templateFormId}/form-fields`,
        { templateFormFields },
    );
}

export async function updateFormFields(
    quoteTemplateId: number | string,
    templateFormId: number | string,
    templateFormFieldId: number | string,
    payload: TemplateFormFieldPayload,
) {
    return axiosInstance.put<TemplateFormField>(
        `api/v1/quote-templates/${quoteTemplateId}/template-forms/${templateFormId}/form-fields/${templateFormFieldId}`,
        payload,
    );
}

export async function updateOrder(
    quoteTemplateId: number | string,
    templateFormId: string | undefined,
    templateFormFieldId: number | string,
    payload: {
        preOrder: number;
        newOrder: number;
    },
) {
    return axiosInstance.put<TemplateFormWithFields>(
        `api/v1/quote-templates/${quoteTemplateId}/template-forms/${templateFormId}/form-fields/${templateFormFieldId}/updateOrder`,
        payload,
    );
}

export async function deleteFormField(
    quoteTemplateId: number | string,
    templateFormFieldId: string,
) {
    return axiosInstance.delete<void>(
        `api/v1/quote-templates/${quoteTemplateId}/${templateFormFieldId}`,
    );
}
